import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ShieldCheck, Lock, Orbit, LifeBuoy } from "lucide-react";

// Same palette the sidebar and the rest of Sanctuary use.
const COLORS = {
  amethyst: "#6D4C92",
  darkAmethyst: "#2D1B44",
  roseGold: "#E5B8AD",
  cream: "#F6EBDD",
  ink: "#4A3F35",
  muted: "#8A7F73",
  hairline: "#EDE8E0",
};

interface Pillar {
  icon: React.ElementType;
  title: string;
  body: string;
  /** Accent drawn from the hero aurora, so each card reads distinctly. */
  accent: string;
  /** Soft fill behind the icon tile. */
  wash: string;
}

// NOTE: title/body are i18n keys (astriaHome.trust.*). Keep this copy in step with the legal pages (components/legal/*).
// Any claim made here is a promise to the user, so it must be one the
// product and the Privacy Policy can actually stand behind.
const PILLARS: Pillar[] = [
  {
    icon: ShieldCheck,
    accent: "#C2569B",
    wash: "linear-gradient(150deg, #FFF2F9 0%, #FCE4F1 100%)",
    title: "astriaHome.trust.grounded.title",
    body: "astriaHome.trust.grounded.body",
  },
  {
    icon: Lock,
    accent: "#6D4C92",
    wash: "linear-gradient(150deg, #F6F1FF 0%, #EADFFB 100%)",
    title: "astriaHome.trust.private.title",
    body: "astriaHome.trust.private.body",
  },
  {
    icon: Orbit,
    accent: "#2F7FB8",
    wash: "linear-gradient(150deg, #EEF7FF 0%, #DCEDFB 100%)",
    title: "astriaHome.trust.precise.title",
    body: "astriaHome.trust.precise.body",
  },
  {
    icon: LifeBuoy,
    accent: "#C9862F",
    wash: "linear-gradient(150deg, #FFF8EC 0%, #FCEBD2 100%)",
    title: "astriaHome.trust.available.title",
    body: "astriaHome.trust.available.body",
  },
];

const TrustSection: React.FC = () => {
  const { t } = useTranslation();
  return (
  <section className="w-full mt-5 sm:mt-6" style={{ marginTop: "2.5rem" }}>
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mx-auto"
      style={{ maxWidth: 900 }}
    >
      {PILLARS.map((pillar, i) => {
        const Icon = pillar.icon;
        return (
          <motion.div
            key={pillar.title}
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{
              duration: 0.45,
              delay: Math.min(i * 0.08, 0.32),
              ease: [0.22, 1, 0.36, 1],
            }}
            className="relative h-full rounded-[22px] bg-white p-5 sm:p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_14px_32px_rgba(45,27,68,0.10)]"
            style={{
              border: `1px solid ${COLORS.hairline}`,
              boxShadow: "0 8px 25px rgba(0,0,0,0.05)",
            }}
          >
            <span
              className="absolute top-0 left-6 right-6 h-[2px] rounded-b"
              style={{
                background: `linear-gradient(90deg, ${pillar.accent}, transparent)`,
              }}
              aria-hidden="true"
            />
            <span
              className="flex items-center justify-center w-11 h-11 rounded-2xl"
              style={{
                background: pillar.wash,
                border: `1px solid ${pillar.accent}33`,
              }}
            >
              <Icon
                className="w-[18px] h-[18px]"
                style={{ color: pillar.accent }}
                strokeWidth={2}
              />
            </span>

            <h3
              className="mt-4 text-[15px] font-semibold leading-snug"
              style={{ color: COLORS.darkAmethyst }}
            >
              {t(pillar.title)}
            </h3>

            <p
              className="mt-2 text-[12.5px] sm:text-[13px] leading-relaxed"
              style={{ color: COLORS.muted }}
            >
              {t(pillar.body)}
            </p>
          </motion.div>
        );
      })}
    </div>
  </section>
  );
};

export default TrustSection;
